import { trackProductAction } from '@/libs/product-analytics';
import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import {
  ErrorCode,
  type ApiResponse,
  type Commission,
  type CommissionDecisionRequest,
  type PaginatedCommissions,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

export type RecurringCommissionDecision = CommissionDecisionRequest['decision'];

export type DecideRecurringCommissionInput = {
  commissionId: string;
  decision: RecurringCommissionDecision;
  reason?: string;
};

const decideRecurringCommission = async ({
  commissionId,
  decision,
  reason,
}: DecideRecurringCommissionInput): Promise<Commission> => {
  const { data } = await api.post<ApiResponse<Commission>>(
    `/dashboard/affiliates/commissions/${commissionId}/decision`,
    { decision, reason }
  );

  if (!data.success || !data.data) throw new Error(data.message);
  return data.data;
};

const decisionErrorKeys: Partial<Record<ErrorCode, string>> = {
  [ErrorCode.E_RECURRING_COMMISSION_NOT_FOUND]: 'commissions.decision.errors.notFound',
  [ErrorCode.E_RECURRING_COMMISSION_REJECTION_REASON_REQUIRED]:
    'commissions.decision.errors.rejectionReasonRequired',
  [ErrorCode.E_RECURRING_COMMISSION_ALREADY_DECIDED]: 'commissions.decision.errors.alreadyDecided',
};

/**
 * Records the brand's line-by-line decision for a recurring commission.
 * Payment is deliberately outside this mutation: the existing payout rail
 * remains responsible for changing `Commission.status` later.
 */
export const useDecideRecurringCommissionAction = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<Commission, AxiosError<ApiResponse>, DecideRecurringCommissionInput>({
    mutationFn: (input) =>
      trackProductAction(
        'commissions',
        input.decision === 'approved' ? 'validate' : 'decline',
        () => decideRecurringCommission(input)
      ),
    onSuccess: async (commission, input) => {
      // Replace the precise history row before refetching. The drawer is fed by
      // this list, so a close/reopen immediately observes the terminal decision.
      queryClient.setQueriesData<PaginatedCommissions>(
        { queryKey: ['commissions', 'history'] },
        (current) =>
          current
            ? {
                ...current,
                data: current.data.map((item) => (item.id === commission.id ? commission : item)),
              }
            : current
      );
      await queryClient.invalidateQueries({ queryKey: ['commissions'] });
      toaster.success({
        title: t(`commissions.decision.toasts.${input.decision}`),
      });
    },
    onError: (error) => {
      const code = error.response?.data?.code as ErrorCode | undefined;
      toaster.error({
        title: t('commissions.decision.toasts.error'),
        description:
          code && decisionErrorKeys[code]
            ? t(decisionErrorKeys[code])
            : t('common.errors.unexpected'),
      });
    },
  });
};
